import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useFirstVisit from '../hooks/useFirstVisit';
import './DashboardTutorial.css';

const SCHRITTE = [
  {
    targetId: 'tutorial-an-kpi',
    pad: 14,
    radius: 16,
    titel: 'Dein Bewerbungs-Cockpit',
    inhalt: (
      <p>
        Analytics ist dafür da, all deine Bewerbungen übersichtlich zu halten.
        Hier siehst du auf einen Blick deinen Rückmeldungsfunnel — Bewerbungen
        gesamt, Antwortquote, Interviews und Zusagen.
      </p>
    ),
    tooltipPos: 'bottom',
  },
  {
    targetId: 'tutorial-an-widget',
    pad: 14,
    radius: 16,
    titel: 'Todos & Kalender',
    inhalt: (
      <p>
        Hier kannst du Todos anlegen, falls du für eine Stelle noch etwas
        erledigen musst. Im Kalender-Tab kannst du direkt mit Google und
        Microsoft synchronisieren, damit wichtige Termine wie Interviews
        automatisch in deinem Kalender landen.
      </p>
    ),
    tooltipPos: 'bottom-left',
  },
  {
    targetId: 'tutorial-an-stellen',
    pad: 14,
    radius: 16,
    titel: 'Alle deine Bewerbungen',
    inhalt: (
      <p>
        Hier siehst du alle Bewerbungen und auf welchem Status sie sind —
        du kannst nach Status filtern. Den Status setzt du selbst: in der
        Stelle, im Dashboard beim Markieren als "Abgeschickt", oder direkt
        hier. Neue Bewerbungen aus CVFlow erscheinen automatisch.
      </p>
    ),
    tooltipPos: 'top',
  },
  {
    targetId: 'tutorial-an-extern',
    pad: 16,
    radius: 999,
    titel: 'Externe Stellen hinzufügen',
    inhalt: (
      <p>
        Du kannst auch Stellen hinzufügen, die du nicht über CVFlow generiert
        hast. So behältst du immer einen zentralen Überblick — gerade bei
        vielen parallelen Bewerbungen ist das entscheidend.
      </p>
    ),
    tooltipPos: 'top-left',
  },
];

function useRect(targetId) {
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  useEffect(() => {
    function tick() {
      const el = document.getElementById(targetId);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect(prev => {
          if (!prev || prev.top !== r.top || prev.left !== r.left ||
              prev.width !== r.width || prev.height !== r.height) {
            return { top: r.top, left: r.left, width: r.width, height: r.height,
                     bottom: r.bottom, right: r.right };
          }
          return prev;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [targetId]);

  return rect;
}

function TutorialOverlay({ schritt, onWeiter, onUeberspringen, gesamt }) {
  const { targetId, pad, radius, titel, inhalt, tooltipPos } = SCHRITTE[schritt];
  const rect = useRect(targetId);

  if (!rect) return null;

  const top    = rect.top    - pad;
  const left   = rect.left   - pad;
  const width  = rect.width  + pad * 2;
  const height = rect.height + pad * 2;

  const TOOLTIP_W = 340;
  let ttStyle = {};

  if (tooltipPos === 'bottom') {
    ttStyle = {
      top:  rect.bottom + pad + 16,
      left: rect.left + rect.width / 2 - TOOLTIP_W / 2,
    };
  } else if (tooltipPos === 'bottom-left') {
    ttStyle = {
      top:   rect.bottom + pad + 16,
      right: window.innerWidth - rect.right - pad,
    };
  } else if (tooltipPos === 'top') {
    ttStyle = {
      bottom: window.innerHeight - rect.top + pad + 16,
      left:   rect.left + rect.width / 2 - TOOLTIP_W / 2,
    };
  } else if (tooltipPos === 'top-left') {
    ttStyle = {
      bottom: window.innerHeight - rect.top + pad + 16,
      right:  window.innerWidth - rect.right - pad,
    };
  }

  if (ttStyle.left !== undefined) {
    ttStyle.left = Math.max(16, Math.min(ttStyle.left, window.innerWidth - TOOLTIP_W - 16));
  }

  return createPortal(
    <div className="dt-root" onClick={e => e.stopPropagation()}>
      <div className="dt-backdrop" />
      <div
        className="dt-spotlight"
        style={{ top, left, width, height, borderRadius: radius }}
      />
      <div className="dt-tooltip" style={{ ...ttStyle, width: TOOLTIP_W }}>
        <div className="dt-tooltip-step">{schritt + 1} / {gesamt}</div>
        <h3 className="dt-tooltip-titel">{titel}</h3>
        <div className="dt-tooltip-inhalt">{inhalt}</div>
        <div className="dt-tooltip-actions">
          <button className="dt-skip-inline" onClick={onUeberspringen}>
            Überspringen
          </button>
          <button className="dt-weiter" onClick={onWeiter}>
            {schritt === gesamt - 1 ? 'Fertig ✓' : 'Weiter →'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AnalyticsTutorial() {
  const { isFirst, dismiss } = useFirstVisit('analytics');
  const [schritt, setSchritt] = useState(0);

  if (!isFirst) return null;

  function weiter() {
    if (schritt < SCHRITTE.length - 1) setSchritt(s => s + 1);
    else dismiss();
  }

  return (
    <TutorialOverlay
      schritt={schritt}
      onWeiter={weiter}
      onUeberspringen={dismiss}
      gesamt={SCHRITTE.length}
    />
  );
}
